/* Crucial Tracking — support hours for the visitor's time zone.
   Support runs seven days on one rule (Kris, 2026-10-24), from Theme
   settings > Crucial Tracking and carried on every hours span by
   snippets/ct-bind and snippets/ct-hours as
     data-ct-hours-rule="<open, Perth>|<open, elsewhere>|<Mon–Fri close>|<Sat–Sun close>"
   e.g. "7:00|8:30|20|19":
   - opening is the visitor's own clock: a browser in Perth (zone
     Australia/Perth, or its alias Australia/West) opens at the Perth time;
     any other Australia/* zone opens at the other time on its own local
     clock, whatever its offset.
   - closing is Perth's clock: Mon–Fri and Sat–Sun each close on a whole
     hour in Perth, and a visitor further east sees that same instant on
     their own clock, so later (an 8pm close is 11pm in Sydney in summer,
     9:30pm in Darwin). Which close applies is decided by Perth's date at
     the moment: Perth is never ahead of another Australian zone, so the
     days line up.

   For a browser in any Australia/* zone:
   - every [data-ct-hours-text] span (ct-bind's hook span, "short" or
     "long") gets the wording composed from the rule in the visitor's
     local time: "Mon–Fri 8:30am–11pm, Sat–Sun 8:30am–10pm" (long:
     "Mon–Fri 8:30am–11:00pm, Sat–Sun 8:30am–10:00pm"), or "7 days, …"
     when the two ranges come out the same. Each local close is the Perth
     closing instant for today's date, read on the visitor's clock.
   - every [data-ct-hours] span (snippets/ct-hours) gets the status: "Open
     now until 11pm" while open, otherwise "Opens 8:30am" (later today) or
     "Opens 8:30am tomorrow", today being the visitor's own date. A close
     that falls past the visitor's midnight still belongs to the day it
     closes. A 'line' span has its wording replaced by the status; a
     'note' span (hidden, empty) is filled and shown after the sentence
     that already states the hours.
   A browser outside Australia, or with no resolvable zone: nothing is
   touched. The printed wording (settings.ct_contact_hours / _long) is true
   in every Australian zone and stands alone.

   Nothing written names a time zone, a city or "your time": the hours
   shown are already the visitor's own.

   Every wall-clock reading goes through the Intl API with the zone's own
   rules, so daylight saving is applied and no offset is typed here.
   Without Intl support, or with a rule that does not make sense (missing,
   not H:MM, a close outside 1–24, an opening not before both closes —
   ct-bind applies the same check), that span is left alone. Re-runs each
   minute and on section reloads in the theme editor. Idempotent, and
   textContent only. */

(function () {
  if (window.__ctHoursInit) return;
  window.__ctHoursInit = true;

  if (!window.Intl || typeof Intl.DateTimeFormat !== 'function') return;

  var PERTH = 'Australia/Perth';
  var PERTH_ALIASES = { 'Australia/Perth': true, 'Australia/West': true };
  var MINUTE = 60000;

  var formats = {};
  function formatter(zone) {
    if (!formats[zone]) {
      formats[zone] = new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        hourCycle: 'h23',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      });
    }
    return formats[zone];
  }

  // What the zone's clock reads at this instant: { y, m, d, h, mi }. A
  // browser that ignores hourCycle (older Safari and Chrome) hands back a
  // 12-hour reading with a dayPeriod part; that throws, so the caller's
  // catch leaves the static wording standing.
  function wall(ms, zone) {
    var out = {};
    formatter(zone)
      .formatToParts(new Date(ms))
      .forEach(function (part) {
        out[part.type] = part.value;
      });
    if (out.dayPeriod !== undefined) throw new Error('12-hour clock');
    return {
      y: Number(out.year),
      m: Number(out.month),
      d: Number(out.day),
      h: Number(out.hour) % 24,
      mi: Number(out.minute),
    };
  }

  // How far the zone's clock is ahead of UTC at this instant, in ms.
  function offset(ms, zone) {
    var w = wall(ms, zone);
    return Date.UTC(w.y, w.m - 1, w.d, w.h, w.mi) - Math.floor(ms / MINUTE) * MINUTE;
  }

  // The instant the zone's clock reads this date and time. The second pass
  // follows a daylight-saving change between the guess and the answer. An
  // hour of 24 is midnight at the end of the day.
  function instant(day, h, mi, zone) {
    var guess = Date.UTC(day.y, day.m - 1, day.d, h, mi);
    var first = guess - offset(guess, zone);
    return guess - offset(first, zone);
  }

  function dateOf(w) {
    return { y: w.y, m: w.m, d: w.d };
  }

  function nextDay(day) {
    var t = new Date(Date.UTC(day.y, day.m - 1, day.d + 1));
    return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
  }

  function sameDay(a, b) {
    return a.y === b.y && a.m === b.m && a.d === b.d;
  }

  function isWeekend(day) {
    var weekday = new Date(Date.UTC(day.y, day.m - 1, day.d)).getUTCDay();
    return weekday === 0 || weekday === 6;
  }

  // A 12-hour clock time. Short drops ":00" (7am, 8:30am, 11pm); long
  // always carries the minutes (7:00am, 8:30am, 11:00pm).
  function clockTime(h, mi, long) {
    var shown = h % 12;
    if (shown === 0) shown = 12;
    var text = long || mi ? shown + ':' + (mi < 10 ? '0' : '') + mi : String(shown);
    return text + (h % 24 < 12 ? 'am' : 'pm');
  }

  // An opening time, "H:MM" on a 24-hour clock, or null.
  function openingTime(text) {
    var match = /^(\d{1,2}):(\d{2})$/.exec(text);
    if (!match) return null;
    var h = Number(match[1]);
    var mi = Number(match[2]);
    if (h > 23 || mi > 59) return null;
    return { h: h, mi: mi };
  }

  // A closing hour, a whole hour 1–24, or NaN.
  function closingHour(text) {
    if (!/^\d{1,2}$/.test(text)) return NaN;
    var h = Number(text);
    return h >= 1 && h <= 24 ? h : NaN;
  }

  // "7:00|8:30|20|19" as a rule, or null when it does not make sense.
  function parseRule(text) {
    var parts = String(text || '').split('|');
    if (parts.length !== 4) return null;
    var rule = {
      openPerth: openingTime(parts[0]),
      open: openingTime(parts[1]),
      closeWeekday: closingHour(parts[2]),
      closeWeekend: closingHour(parts[3]),
    };
    if (!rule.openPerth || !rule.open || isNaN(rule.closeWeekday) || isNaN(rule.closeWeekend)) return null;
    var firstClose = Math.min(rule.closeWeekday, rule.closeWeekend) * 60;
    if (rule.openPerth.h * 60 + rule.openPerth.mi >= firstClose) return null;
    if (rule.open.h * 60 + rule.open.mi >= firstClose) return null;
    return rule;
  }

  // The wording and the status for one rule, in one zone, at one instant.
  function compute(rule, zone, now) {
    var open = PERTH_ALIASES[zone] === true ? rule.openPerth : rule.open;
    var localToday = dateOf(wall(now, zone));

    // A Perth closing hour for today's date, on the visitor's clock.
    function localClose(hour) {
      return wall(instant(localToday, hour, 0, PERTH), zone);
    }
    var weekdayClose = localClose(rule.closeWeekday);
    var weekendClose = localClose(rule.closeWeekend);
    function wording(long) {
      var opens = clockTime(open.h, open.mi, long);
      var weekday = opens + '–' + clockTime(weekdayClose.h, weekdayClose.mi, long);
      var weekend = opens + '–' + clockTime(weekendClose.h, weekendClose.mi, long);
      return weekday === weekend ? '7 days, ' + weekday : 'Mon–Fri ' + weekday + ', Sat–Sun ' + weekend;
    }

    // One day's hours: open on the visitor's clock, close on Perth's.
    function hoursOn(day) {
      return {
        opens: instant(day, open.h, open.mi, zone),
        closes: instant(day, isWeekend(day) ? rule.closeWeekend : rule.closeWeekday, 0, PERTH),
      };
    }
    var perthToday = dateOf(wall(now, PERTH));
    var today = hoursOn(perthToday);
    var status = null;
    if (today.opens < today.closes) {
      if (now >= today.opens && now < today.closes) {
        var closes = wall(today.closes, zone);
        status = 'Open now until ' + clockTime(closes.h, closes.mi, false);
      } else {
        var next = now < today.opens ? today : hoursOn(nextDay(perthToday));
        var opensOn = dateOf(wall(next.opens, zone));
        status = 'Opens ' + clockTime(open.h, open.mi, false) + (sameDay(opensOn, localToday) ? '' : ' tomorrow');
      }
    }

    return { short: wording(false), long: wording(true), status: status };
  }

  function visitorZone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (e) {
      return '';
    }
  }

  function update() {
    var zone = visitorZone();
    if (zone.indexOf('Australia/') !== 0) return;
    var now = new Date().getTime();

    // Every span carries the same rule; each distinct one is worked out once.
    var results = {};
    function resultFor(el) {
      var text = el.getAttribute('data-ct-hours-rule') || '';
      if (!Object.prototype.hasOwnProperty.call(results, text)) {
        var rule = parseRule(text);
        var result = null;
        if (rule) {
          try {
            result = compute(rule, zone, now);
          } catch (e) {
            result = null;
          }
        }
        results[text] = result;
      }
      return results[text];
    }

    var texts = document.querySelectorAll('[data-ct-hours-text]');
    for (var t = 0; t < texts.length; t++) {
      var found = resultFor(texts[t]);
      if (!found) continue;
      var words = texts[t].getAttribute('data-ct-hours-text') === 'long' ? found.long : found.short;
      if (texts[t].textContent !== words) texts[t].textContent = words;
    }

    var spans = document.querySelectorAll('[data-ct-hours]');
    for (var i = 0; i < spans.length; i++) {
      var span = spans[i];
      var hours = resultFor(span);
      if (!hours || !hours.status) continue;
      var line = hours.status;
      if (span.getAttribute('data-ct-hours') === 'note') {
        line = (span.getAttribute('data-ct-hours-sep') || '') + line + (span.getAttribute('data-ct-hours-end') || '');
        if (span.textContent !== line) span.textContent = line;
        span.hidden = false;
      } else if (span.textContent !== line) {
        span.textContent = line;
      }
    }
  }

  update();
  window.setInterval(update, 60000);
  document.addEventListener('shopify:section:load', update);
})();
